/**
 * Annual pricing by geography.
 *
 * Mirrors GEO_TIER_1_COUNTRIES and GEO_TIER_3_COUNTRIES in
 * backend/altar/integrations/autumn/products.py. The backend resolves the tier again
 * from the country code the client reports, so this copy only decides which annual
 * price to display; it never names an Autumn product id.
 */

export type GeoTier = "t1" | "t2" | "t3"

const GEO_TIER_1_COUNTRIES = new Set([
  "DE", "AU", "CA", "FR", "US", "ES", "GB",
  "AD", "AT", "BE", "BG", "CH", "CY", "CZ", "DK", "EE", "FI", "GI", "GR", "HR",
  "HU", "IE", "IS", "IT", "LI", "LT", "LU", "LV", "MC", "MT", "NL", "NO", "PL",
  "PT", "RO", "SE", "SI", "SK", "SM",
])

const GEO_TIER_3_COUNTRIES = new Set(["BR", "MX", "IN"])

/** Monthly-equivalent annual price per plan, by geo tier. */
export const ANNUAL_PRICES: Record<string, Record<GeoTier, number>> = {
  creator_plan: { t1: 14.5, t2: 12, t3: 10 },
  business_plan: { t1: 19.5, t2: 16.5, t3: 14 },
}

const GEO_TIER_STORAGE_KEY = "transcriptr_geo_tier"

const COUNTRY_ENDPOINT = "https://get.geojs.io/v1/ip/country.json"

/** The geo tier a country falls in; an unknown or missing country is tier 1. */
export function countryToGeoTier(countryCode: string | null | undefined): GeoTier {
  const code = (countryCode || "").trim().toUpperCase()
  if (GEO_TIER_3_COUNTRIES.has(code)) return "t3"
  if (code && !GEO_TIER_1_COUNTRIES.has(code)) return "t2"
  return "t1"
}

/** The annual price to show for a plan in a tier; undefined when the plan has no annual. */
export function annualPrice(planId: string, tier: GeoTier): number | undefined {
  return ANNUAL_PRICES[planId]?.[tier]
}

/** The yearly total billed for a plan in a tier; undefined when the plan has no annual. */
export function annualTotal(planId: string, tier: GeoTier): number | undefined {
  const monthly = annualPrice(planId, tier)
  return monthly === undefined ? undefined : Math.round(monthly * 12)
}

function readCachedCountry(): string | null {
  try {
    return window.localStorage.getItem(GEO_TIER_STORAGE_KEY)
  } catch {
    return null
  }
}

function cacheCountry(code: string): void {
  try {
    window.localStorage.setItem(GEO_TIER_STORAGE_KEY, code)
  } catch {
    // Storage is unavailable in private modes; the lookup simply repeats next time.
  }
}

/**
 * The caller's country code, cached in localStorage. Resolves to null rather than
 * throwing, so a blocked or failing lookup leaves the caller on tier 1.
 */
export async function fetchCountryCode(): Promise<string | null> {
  const cached = readCachedCountry()
  if (cached) return cached
  try {
    const response = await fetch(COUNTRY_ENDPOINT)
    if (!response.ok) return null
    const body = (await response.json()) as { country_code?: string }
    const code = (body?.country_code || "").trim().toUpperCase()
    if (!code) return null
    cacheCountry(code)
    return code
  } catch {
    return null
  }
}
